/**
 * Contratos de domínio do módulo ELO (integração interdisciplinar) — Fase 1
 * local-first, PT-BR. Espelha a gramática do Pitada/Compasso, traduzida para a
 * coordenação de equipe: "biblioteca" = Profissional, "evento" = NotaSessao.
 *
 * Decisões (ver docs/cerebro/decisoes.md):
 *  - ADR-12: Fase 1 é local-first; o cuidador registra (3ª pessoa) o que cada
 *    profissional fez, via `Origem="profissional"` que já existe em ../types.
 *  - ADR-13: a escolha alimentar (preparar refeição/modo escolha) fica reservada;
 *    o gancho é NotaSessao.alimento_id + degrau_alvo (objetivo compartilhado).
 *  - A escada 0–9 (`PassoEscada`) é REUSADA como idioma das disciplinas — sem enum novo.
 *  - Campos de Fase 2 (estado, versao_de, sensibilidade) nascem aqui INERTES
 *    (forma-primeiro); o sync/anel/@menção entram depois (ADR-11).
 *
 * Em caso de dúvida, o código é a fonte da verdade.
 */
import type { EspecialidadeProf, PassoEscada } from "../types";
import { PassoEscada as Passo } from "../types";

// Ordem de exibição das disciplinas (fono e TO primeiro — destravam a escada).
export const ESPECIALIDADES_ORDEM: EspecialidadeProf[] = [
  "fono",
  "to",
  "psico",
  "nutri",
  "medico",
  "outro",
];

/** Rótulo curto da disciplina (chips, timeline). */
export const ESPECIALIDADE_CURTA: Record<EspecialidadeProf, string> = {
  fono: "Fono",
  to: "TO",
  psico: "Psico",
  nutri: "Nutri",
  medico: "Médico",
  outro: "Outro",
};

/** Classe CSS de cor por disciplina (nutri = ardósia, nunca vermelho — ADR-10). */
export const DISCIPLINA_CLASSE: Record<EspecialidadeProf, string> = {
  fono: "disc-fono",
  to: "disc-to",
  psico: "disc-psico",
  nutri: "disc-nutri",
  medico: "disc-medico",
  outro: "disc-outro",
};

// ──────────────────────────────────────────────────────────────────────────
// Enums / unions
// ──────────────────────────────────────────────────────────────────────────

/** Estado da nota. F1 nasce "publicada" e é editável; rascunho/versão são F2. */
export type EstadoNota = "rascunho" | "publicada";

/** Sensibilidade de um anexo clínico. `restrito_anel` (antes/depois) fica fora do export. */
export type SensibilidadeAnexo = "normal" | "restrito_anel";

export type TipoAnexo = "foto" | "video" | "audio" | "documento";

export const TIPO_ANEXO_LABEL: Record<TipoAnexo, string> = {
  foto: "foto",
  video: "vídeo",
  audio: "áudio",
  documento: "documento",
};

// ──────────────────────────────────────────────────────────────────────────
// Entidades
// ──────────────────────────────────────────────────────────────────────────

/** Anexo clínico — Blob local (tabela `foto`), com sensibilidade. */
export interface AnexoClinico {
  blob_id: string; // ref na tabela foto (Blob)
  tipo: TipoAnexo;
  sensibilidade: SensibilidadeAnexo;
}

/** Profissional — a "biblioteca" do Elo, cadastrado 1×. Na F1 é um contato rotulado. */
export interface Profissional {
  id: string;
  nome: string;
  especialidade: EspecialidadeProf;
  registro_classe: string | null; // CRFa / CREFITO / CRP / CRN
  contato: string | null; // e-mail/telefone (convite ao anel — F2)
  arquivado: boolean;
  criado_em: string; // ISO 8601
}

/**
 * Nota de Sessão — o "evento" do Elo (rico, por sessão). Só `objetivo` +
 * `especialidade` são obrigatórios; o resto é opcional e nunca bloqueia.
 */
export interface NotaSessao {
  id: string;
  crianca_id: string;
  profissional_id: string;
  especialidade: EspecialidadeProf; // denormalizado → cor/chip na timeline
  data_sessao: string; // ISO 8601
  objetivo: string; // OBRIGATÓRIO
  alimento_id: string | null; // liga à escada de um alimento (opcional)
  degrau_alvo: PassoEscada | null; // alvo na escada (opcional)
  degrau_observado: PassoEscada | null; // resultado → gera Oferta(origem=profissional)
  atividades: string[];
  resultados: string | null;
  orientacoes_casa: string | null; // o que o cuidador lê em destaque
  // marcação do CUIDADOR de que a orientação foi feita em casa: null = não feito;
  // ISO 8601 = quando marcou. Toggle na tela "pra fazer em casa" (P0.4b).
  orientacao_feita_em: string | null;
  observacoes: string | null;
  anexos: AnexoClinico[];
  oferta_id_gerada: string | null; // rastreio da Oferta criada pelo resultado
  estado: EstadoNota; // F1: "publicada" (inerte; rascunho/versão = F2)
  versao_de: string | null; // F1: null (imutabilidade = F2)
  criado_em: string; // ISO 8601
}

// ──────────────────────────────────────────────────────────────────────────
// Derivados (CALCULADOS, não persistidos) — o "estado da equipe"
// ──────────────────────────────────────────────────────────────────────────

export interface DerivadosEquipe {
  crianca_id: string;
  n_notas: number;
  ultima_sessao: string | null; // ISO da nota mais recente
  disciplinas: EspecialidadeProf[]; // que registraram nota
  // por disciplina presente: dias desde a última nota dela (null = nunca)
  dias_sem_nota: Partial<Record<EspecialidadeProf, number>>;
}

/**
 * Um alimento em trabalho pela equipe e de quem é a responsabilidade AGORA de
 * evoluí-lo na escada (derivada do degrau atual). Importa o id do alimento — a
 * tela resolve nome/imagem reusando o Pitada.
 */
export interface ItemResponsabilidade {
  alimento_id: string;
  degrau_atual: PassoEscada | null; // posição na escada (ofertas + degrau observado)
  responsavel: EspecialidadeProf | null; // TO (0–4) ou fono (5–9); null sem degrau ainda
  disciplinas: EspecialidadeProf[]; // que já registraram nota nesse alimento
  ultima_nota: string | null; // ISO da nota mais recente sobre o alimento
  dias_desde_ultima: number | null;
}

/** Faixa de responsabilidade da escada (didático, não trava — §3 do estudo). */
export const DEGRAU_TO_MAX = Passo.Cheira; // 4 — TO destrava 0–4
